import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Check, MapPin, Award } from "lucide-react";
import CommunitySection from "@/components/community/CommunitySection";

export default function ModuleDetail() {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/modules/${moduleId}`).then((r) => setModule(r.data)).catch(() => {});
  }, [moduleId]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/progress").then((r) => {
        const set = new Set(r.data.completed.filter((c) => c.module_id === moduleId).map((c) => c.chapter_id));
        setCompleted(set);
      }).catch(() => {});
    }
  }, [user, moduleId]);

  if (!module) return <div className="max-w-4xl mx-auto p-12 text-[#525252]">Loading…</div>;

  const allDone = module.chapters.length > 0 && completed.size === module.chapters.length;

  return (
    <div>
      <div className="border-b border-[#E5E5E0]">
        <div className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Link data-testid="module-back" to="/modules" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> All modules
            </Link>
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <div className="font-mono text-5xl font-medium text-[#C5A059]">{module.number}</div>
              <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
                module.track === "india" ? "bg-[#C5A059] text-[#0A0A0A]"
                : module.track === "dubai" ? "bg-white text-[#0A0A0A] border border-[#0A0A0A]"
                : "bg-[#0A0A0A] text-white"
              }`} data-testid="module-track-chip">
                {module.track === "india" ? "India Track" : module.track === "dubai" ? "Dubai Track" : "Foundations"}
              </span>
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#525252]">
                <MapPin className="w-3 h-3" /> {module.region}
              </div>
            </div>
            <h1 data-testid="module-title" className="font-serif text-4xl lg:text-5xl font-black text-[#0A0A0A] tracking-tight leading-tight mb-4">
              {module.title}
            </h1>
            <p className="font-serif italic text-xl text-[#525252] mb-6">{module.subtitle}</p>
            <p className="text-[#0A0A0A] leading-relaxed text-lg max-w-2xl">{module.summary}</p>

            {user && user !== false && allDone && (
              <Link
                data-testid="module-certificate-cta"
                to={`/certificate/${module.id}`}
                className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-[#C5A059] text-[#0A0A0A] hover:bg-[#A88643] transition-colors font-medium"
              >
                <Award className="w-4 h-4" /> View your certificate
              </Link>
            )}
          </div>

          <div className="lg:col-span-5">
            <img src={module.hero_image} alt="" className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-8">Chapters</div>
        <div className="border-t border-[#E5E5E0]" data-testid="chapter-list">
          {module.chapters.map((c, i) => {
            const done = completed.has(c.id);
            return (
              <Link
                key={c.id}
                data-testid={`chapter-link-${c.id}`}
                to={`/modules/${module.id}/chapters/${c.id}`}
                className="flex items-center gap-6 py-6 border-b border-[#E5E5E0] hover:bg-[#F5F5F0] px-4 -mx-4 transition-colors group"
              >
                <div className="font-mono text-sm text-[#C5A059] w-12">{c.number}</div>
                <div className="flex-1">
                  <div className="font-serif text-xl font-semibold text-[#0A0A0A] group-hover:text-[#000000]">{c.title}</div>
                  <div className="text-xs font-mono uppercase tracking-wider text-[#525252] mt-1">{c.reading_time} min read</div>
                </div>
                {done && (
                  <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#C5A059]">
                    <Check className="w-4 h-4" /> Read
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      <CommunitySection moduleId={module.id} moduleTitle={module.title} />
    </div>
  );
}
